/**
 * BoardLayouts.tsx — Layout variants para el tablero público de pacientes
 * Cada componente acepta el mismo set de props para ser intercambiables.
 */

import React, { useEffect, useState, useRef } from 'react';
import { Patient, Room, PatientStatus, AdAsset, AdSettings } from '../../types';
import AdBanner from '../AdBanner';
import AdTicker from '../AdTicker';

// ─── Shared Prop Types ────────────────────────────────────────

export interface ThemeTokens {
  bgMain: string;
  textMain: string;
  textMuted: string;
  textSubtle: string;
  cardBg: string;
  cardBorder: string;
  borderMain: string;
  borderLight: string;
  divider: string;
  progressBg: string;
  progressSecondary: string;
  pillBg: string;
  cardHover: string;
}

export interface BoardLayoutProps {
  patients: Patient[];
  rooms: Room[];
  theme: 'dark' | 'light';
  t: ThemeTokens;
  primaryColor: string;
  getStatusStyles: (status: PatientStatus, primaryColor: string) => {
    bg: string; border: string; text: string; glow: string; progressBg: string;
  };
  getStatusDisplay: (patient: Patient) => string;
  adAssets: AdAsset[];
  adSettings: AdSettings;
  adsEnabled: boolean;
  hospitalId: string;
}

// ─── Shared Patient Row (reused by LIST layout internally if needed) ──────────

const StatusBadge: React.FC<{
  patient: Patient;
  getStatusStyles: BoardLayoutProps['getStatusStyles'];
  getStatusDisplay: BoardLayoutProps['getStatusDisplay'];
  primaryColor: string;
  theme: 'dark' | 'light';
  size?: 'sm' | 'lg';
}> = ({ patient, getStatusStyles, getStatusDisplay, primaryColor, theme, size = 'sm' }) => {
  const styles = getStatusStyles(patient.currentStatus, primaryColor);
  const isSurgery = patient.currentStatus === PatientStatus.SURGERY;
  const isPostSurgery = patient.currentStatus === PatientStatus.RECOVERY || patient.currentStatus === PatientStatus.EXIT;
  const progress = isPostSurgery ? 100 : (
    patient.currentStatus === PatientStatus.WAITING || patient.currentStatus === PatientStatus.PREPARATION
      ? 0 : (patient.surgeryProgress || 0)
  );

  return (
    <div className="flex flex-col items-center gap-2 w-full">
      <div className="relative">
        {styles.glow !== 'transparent' && (
          <div className="absolute inset-0 blur-xl opacity-40 rounded-full animate-pulse" style={{ backgroundColor: styles.glow }} />
        )}
        <span
          className={`relative px-4 py-1.5 rounded-full font-bold uppercase tracking-widest border backdrop-blur-sm block text-center ${size === 'lg' ? 'text-sm px-8 py-3' : 'text-[10px]'}`}
          style={{ backgroundColor: styles.bg, borderColor: styles.border, color: styles.text }}
        >
          {getStatusDisplay(patient)}
        </span>
      </div>
      {/* Progress bar */}
      <div className="w-full flex items-center gap-2">
        <div className={`flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden`}>
          <div
            className="h-full rounded-full transition-all duration-1000 relative"
            style={{ width: `${progress}%`, backgroundColor: isSurgery ? primaryColor : styles.progressBg }}
          >
            {isSurgery && <div className="absolute inset-0 rounded-full animate-pulse opacity-60" style={{ backgroundColor: primaryColor }} />}
          </div>
        </div>
        <span className="text-[9px] font-mono opacity-40 text-white">{progress}%</span>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// LAYOUT 1: GRID_2COL — Apple-style cards in a 2-column grid
// ─────────────────────────────────────────────────────────────────────────────

export const GridLayout: React.FC<BoardLayoutProps> = ({
  patients, rooms, theme, t, primaryColor, getStatusStyles, getStatusDisplay
}) => {
  return (
    <div className="w-full h-full overflow-hidden p-4 md:p-6">
      {patients.length === 0 ? (
        <div className={`h-full flex flex-col items-center justify-center ${t.textSubtle}`}>
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 opacity-20"
            style={{ backgroundColor: primaryColor + '20', border: `2px solid ${primaryColor}30` }}>
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </div>
          <p className="font-bold tracking-wide text-xl">Sin procedimientos activos</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 md:gap-4 h-full auto-rows-fr">
          {patients.map((patient, idx) => {
            const room = rooms.find(r => r.id === patient.room);
            const styles = getStatusStyles(patient.currentStatus, primaryColor);
            const isPostSurgery = patient.currentStatus === PatientStatus.RECOVERY || patient.currentStatus === PatientStatus.EXIT;
            const isSurgery = patient.currentStatus === PatientStatus.SURGERY;

            return (
              <div
                key={patient.id}
                className={`relative rounded-2xl md:rounded-3xl overflow-hidden flex flex-col justify-between p-4 md:p-5 animate-fade-in border transition-all duration-500`}
                style={{
                  animationDelay: `${idx * 60}ms`,
                  background: theme === 'dark'
                    ? `linear-gradient(135deg, rgba(15,23,42,0.9) 0%, rgba(30,41,59,0.8) 100%)`
                    : `linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.9) 100%)`,
                  borderColor: isSurgery ? primaryColor + '60' : (theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'),
                  boxShadow: isSurgery ? `0 0 24px ${primaryColor}20, 0 4px 16px rgba(0,0,0,0.2)` : '0 4px 16px rgba(0,0,0,0.15)',
                }}
              >
                {/* Subtle glow top-left based on status */}
                <div className="absolute top-0 left-0 w-32 h-32 rounded-full blur-3xl opacity-10 pointer-events-none"
                  style={{ backgroundColor: styles.glow !== 'transparent' ? styles.glow : primaryColor }} />

                {/* Header: ID + Room */}
                <div className="flex items-start justify-between mb-3 relative z-10">
                  <div>
                    <div
                      className={`font-black tracking-tight leading-none mb-1 ${t.textMain}`}
                      style={{ fontSize: 'clamp(1.4rem, 2.5vw, 2.5rem)' }}
                    >
                      {patient.id}
                    </div>
                    <div className={`text-[10px] font-bold uppercase tracking-widest ${t.textMuted} truncate max-w-[120px]`}>
                      {patient.procedureName || 'Procedimiento'}
                    </div>
                  </div>
                  <div
                    className="px-2 py-1 rounded-xl font-mono font-bold text-[10px] border shrink-0"
                    style={{
                      backgroundColor: primaryColor + '15',
                      borderColor: primaryColor + '30',
                      color: primaryColor
                    }}
                  >
                    {isPostSurgery ? 'Rec.' : (room?.name || '---')}
                  </div>
                </div>

                {/* Doctor */}
                {patient.doctorName && (
                  <div className={`text-[11px] ${t.textSubtle} font-medium mb-3 truncate relative z-10`}>
                    {patient.doctorName}
                  </div>
                )}

                {/* Status Badge + Progress */}
                <div className="relative z-10">
                  <StatusBadge
                    patient={patient}
                    getStatusStyles={getStatusStyles}
                    getStatusDisplay={getStatusDisplay}
                    primaryColor={primaryColor}
                    theme={theme}
                  />
                </div>

                {/* Doctor En Camino indicator */}
                {patient.isDoctorEnCamino && (
                  <div className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-amber-400 animate-ping" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// LAYOUT 2: SIDEBAR_AD — Patient list left (70%) + sticky ad panel right (30%)
// ─────────────────────────────────────────────────────────────────────────────

export const SidebarAdLayout: React.FC<BoardLayoutProps> = ({
  patients, rooms, theme, t, primaryColor, getStatusStyles, getStatusDisplay,
  adAssets, adSettings, adsEnabled, hospitalId
}) => {
  const activeAds = adAssets.filter(a => a.isActive && (a.type === 'BANNER_IMAGE' || a.type === 'FULLSCREEN_SLIDE' || a.type === 'BANNER_TEXT'));
  const [adIndex, setAdIndex] = useState(0);

  useEffect(() => {
    if (activeAds.length < 2) return;
    const timer = setInterval(() => {
      setAdIndex(i => (i + 1) % activeAds.length);
    }, (activeAds[adIndex]?.displayTimeSec || 15) * 1000);
    return () => clearInterval(timer);
  }, [activeAds.length, adIndex]);

  const currentAd = activeAds[adIndex];

  return (
    <div className="w-full h-full flex overflow-hidden">
      {/* LEFT: Patient List — 70% */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Column Headers */}
        <div
          className={`hidden md:grid grid-cols-12 gap-4 px-6 py-3 ${t.cardBg} border-b ${t.borderMain} font-bold ${t.textSubtle} uppercase tracking-widest`}
          style={{ fontSize: 'clamp(0.55rem, 0.65vw, 0.75rem)' }}
        >
          <div className="col-span-3">Paciente / Procedimiento</div>
          <div className="col-span-2">Doctor</div>
          <div className="col-span-2">Quirófano</div>
          <div className="col-span-2 text-center">Progreso</div>
          <div className="col-span-3 text-right">Estatus</div>
        </div>

        <div className={`flex-1 overflow-hidden divide-y ${t.divider}`}>
          {patients.length === 0 ? (
            <div className={`h-full flex flex-col items-center justify-center ${t.textSubtle}`}>
              <p className="font-bold text-lg">Sin procedimientos activos</p>
            </div>
          ) : patients.map((patient, idx) => {
            const room = rooms.find(r => r.id === patient.room);
            const styles = getStatusStyles(patient.currentStatus, primaryColor);
            const isPostSurgery = patient.currentStatus === PatientStatus.RECOVERY || patient.currentStatus === PatientStatus.EXIT;
            const isSurgery = patient.currentStatus === PatientStatus.SURGERY;
            const progress = isPostSurgery ? 100 : (
              [PatientStatus.WAITING, PatientStatus.PREPARATION].includes(patient.currentStatus) ? 0 : (patient.surgeryProgress || 0)
            );

            return (
              <div
                key={patient.id}
                className={`flex items-center gap-3 px-5 py-4 ${t.cardHover} transition-all duration-300 animate-fade-in`}
                style={{ animationDelay: `${idx * 50}ms`, borderLeft: patient.isDoctorEnCamino ? '3px solid #fbbf24' : '3px solid transparent' }}
              >
                {/* ID + Procedure */}
                <div className="flex-1 min-w-0">
                  <div className={`font-black ${t.textMain} truncate`} style={{ fontSize: 'clamp(1rem, 1.8vw, 1.8rem)' }}>
                    {patient.id}
                  </div>
                  <div className={`text-[10px] ${t.textMuted} truncate`}>{patient.procedureName || 'Procedimiento'}</div>
                </div>
                {/* Doctor */}
                <div className={`hidden md:block w-24 ${t.textMuted} text-xs truncate`}>{patient.doctorName || '---'}</div>
                {/* Room */}
                <div className="w-20">
                  <span className={`text-xs font-mono font-bold px-2 py-1 rounded-lg ${t.cardBg} border ${t.borderMain} ${t.textMain}`}>
                    {isPostSurgery ? 'Rec.' : (room?.name || '---')}
                  </span>
                </div>
                {/* Progress */}
                <div className="w-20 hidden md:block">
                  <div className={`h-1.5 rounded-full ${t.progressBg} overflow-hidden mb-1`}>
                    <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${progress}%`, backgroundColor: isSurgery ? primaryColor : styles.progressBg }}>
                      {isSurgery && <div className="absolute inset-0 animate-pulse opacity-60" style={{ backgroundColor: primaryColor }} />}
                    </div>
                  </div>
                  <span className={`text-[9px] font-mono ${t.textSubtle}`}>{progress}%</span>
                </div>
                {/* Status */}
                <div className="shrink-0">
                  <span className="text-[10px] font-bold uppercase tracking-wide px-3 py-1.5 rounded-full border"
                    style={{ backgroundColor: styles.bg, borderColor: styles.border, color: styles.text }}>
                    {getStatusDisplay(patient)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* RIGHT: Ad Panel — 30% */}
      <div
        className={`w-[30%] shrink-0 flex flex-col border-l ${t.borderMain} overflow-hidden relative`}
        style={{ background: theme === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(248,250,252,1)' }}
      >
        {/* Ad glow overlay */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(ellipse at top, ${primaryColor}10, transparent 70%)` }} />

        {adsEnabled && currentAd ? (
          <div className="flex-1 flex flex-col p-4 relative z-10 gap-3">
            {/* Ad Header */}
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: primaryColor }} />
              <span className={`text-[9px] font-bold uppercase tracking-widest ${t.textSubtle}`}>Anuncio</span>
            </div>

            {/* Ad Content */}
            <div
              key={adIndex}
              className="flex-1 rounded-2xl overflow-hidden relative flex items-center justify-center animate-fade-in"
              style={{ border: `1px solid ${primaryColor}20` }}
            >
              {currentAd.imageUrl ? (
                <img
                  src={currentAd.imageUrl}
                  alt={currentAd.imageAlt || 'Anuncio'}
                  className="w-full h-full object-cover"
                />
              ) : currentAd.text ? (
                <div
                  className="w-full h-full flex items-center justify-center p-6 text-center font-bold"
                  style={{ background: `linear-gradient(135deg, ${primaryColor}20, ${primaryColor}05)`, color: theme === 'dark' ? 'white' : '#1e293b' }}
                >
                  <p style={{ fontSize: 'clamp(0.9rem, 1.8vw, 1.8rem)', lineHeight: 1.3 }}>{currentAd.text}</p>
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center opacity-20">
                  <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 14H4V6h16v11z"/>
                  </svg>
                </div>
              )}
            </div>

            {/* Ad dots (pagination indicator) */}
            {activeAds.length > 1 && (
              <div className="flex justify-center gap-1.5">
                {activeAds.map((_, i) => (
                  <div
                    key={i}
                    className="h-1 rounded-full transition-all duration-500"
                    style={{
                      width: i === adIndex ? '16px' : '4px',
                      backgroundColor: i === adIndex ? primaryColor : (theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)')
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          // No ads — show a premium hospital branding panel
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center relative z-10">
            <div
              className="w-20 h-20 rounded-3xl flex items-center justify-center mb-4 shadow-2xl"
              style={{ backgroundColor: primaryColor + '20', border: `2px solid ${primaryColor}40` }}
            >
              <svg className="w-10 h-10" fill="none" stroke={primaryColor} viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
              </svg>
            </div>
            <p className={`font-bold ${t.textMuted} text-xs uppercase tracking-widest`}>
              Espacio Publicitario
            </p>
            <p className={`text-[10px] ${t.textSubtle} mt-1`}>
              Activa anuncios en el panel de administración
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// LAYOUT 3: FOCUS_SINGLE — Full-screen, one patient at a time with Apple TV style
// ─────────────────────────────────────────────────────────────────────────────

export const FocusSingleLayout: React.FC<BoardLayoutProps> = ({
  patients, rooms, theme, t, primaryColor, getStatusStyles, getStatusDisplay
}) => {
  const [focusIdx, setFocusIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  // Auto-rotate every 8s with fade transition
  useEffect(() => {
    if (patients.length <= 1) return;
    const timer = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setFocusIdx(i => (i + 1) % patients.length);
        setVisible(true);
      }, 600);
    }, 8000);
    return () => clearInterval(timer);
  }, [patients.length]);

  if (patients.length === 0) {
    return (
      <div className={`h-full flex flex-col items-center justify-center ${t.textSubtle}`}>
        <div className="w-20 h-20 rounded-3xl flex items-center justify-center mb-5 opacity-20"
          style={{ backgroundColor: primaryColor + '20', border: `2px solid ${primaryColor}30` }}>
          <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        </div>
        <p className="font-bold text-2xl tracking-tight">Sin procedimientos activos</p>
      </div>
    );
  }

  const patient = patients[Math.min(focusIdx, patients.length - 1)];
  const room = rooms.find(r => r.id === patient.room);
  const styles = getStatusStyles(patient.currentStatus, primaryColor);
  const isSurgery = patient.currentStatus === PatientStatus.SURGERY;
  const isPostSurgery = patient.currentStatus === PatientStatus.RECOVERY || patient.currentStatus === PatientStatus.EXIT;
  const progress = isPostSurgery ? 100 : (
    [PatientStatus.WAITING, PatientStatus.PREPARATION].includes(patient.currentStatus) ? 0 : (patient.surgeryProgress || 0)
  );

  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center overflow-hidden">
      {/* Background glow */}
      <div
        className="absolute inset-0 pointer-events-none transition-all duration-1000"
        style={{
          background: `radial-gradient(ellipse at center, ${isSurgery ? primaryColor : styles.glow !== 'transparent' ? styles.glow : primaryColor}18 0%, transparent 70%)`
        }}
      />

      {/* Animated noise texture overlay for depth */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.65\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'1\'/%3E%3C/svg%3E")', backgroundSize: '150px' }} />

      {/* Main Content */}
      <div
        className={`relative z-10 flex flex-col items-center text-center px-8 transition-all duration-500 max-w-3xl w-full ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      >
        {/* Patient ID */}
        <div
          className={`font-black tracking-tighter ${t.textMain} mb-3`}
          style={{
            fontSize: 'clamp(4rem, 10vw, 9rem)',
            textShadow: isSurgery ? `0 0 60px ${primaryColor}60` : 'none',
            letterSpacing: '-0.02em'
          }}
        >
          {patient.id}
        </div>

        {/* Procedure */}
        <div
          className={`font--semibold ${t.textMuted} mb-6 max-w-md`}
          style={{ fontSize: 'clamp(0.9rem, 1.8vw, 1.5rem)' }}
        >
          {patient.procedureName || 'Procedimiento en curso'}
        </div>

        {/* Status Badge — large */}
        <div className="relative mb-8">
          {styles.glow !== 'transparent' && (
            <div className="absolute inset-0 blur-2xl opacity-30 rounded-full animate-pulse scale-130"
              style={{ backgroundColor: styles.glow }} />
          )}
          <span
            className="relative px-10 py-4 rounded-full font-black uppercase tracking-widest border text-lg backdrop-blur-sm block"
            style={{ backgroundColor: styles.bg, borderColor: styles.border, color: styles.text }}
          >
            {getStatusDisplay(patient)}
          </span>
        </div>

        {/* Progress Bar */}
        <div className="w-full max-w-sm mb-6">
          <div className={`h-3 rounded-full ${theme === 'dark' ? 'bg-white/10' : 'bg-black/10'} overflow-hidden mb-2`}>
            <div
              className="h-full rounded-full transition-all duration-1000 relative overflow-hidden"
              style={{ width: `${progress}%`, backgroundColor: isSurgery ? primaryColor : styles.progressBg }}
            >
              {isSurgery && (
                <div className="absolute inset-0 animate-[shimmer_2s_infinite]"
                  style={{ background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)` }} />
              )}
            </div>
          </div>
          <div className={`flex justify-between text-xs font-mono ${t.textSubtle}`}>
            <span>0%</span>
            <span className="font-bold" style={{ color: primaryColor }}>{progress}%</span>
            <span>100%</span>
          </div>
        </div>

        {/* Meta Row: Doctor + Room */}
        <div className="flex items-center gap-6">
          {patient.doctorName && (
            <div className={`flex items-center gap-2 ${t.textMuted}`}
              style={{ fontSize: 'clamp(0.7rem, 1.2vw, 1rem)' }}>
              <svg className="w-4 h-4 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zm-4 7a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              {patient.doctorName}
            </div>
          )}
          {!isPostSurgery && room && (
            <div
              className="flex items-center gap-2 font-mono font-bold px-3 py-1 rounded-lg border"
              style={{
                backgroundColor: primaryColor + '15',
                borderColor: primaryColor + '30',
                color: primaryColor,
                fontSize: 'clamp(0.7rem, 1.2vw, 1rem)'
              }}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              </svg>
              {room.name}
            </div>
          )}
        </div>

        {/* Doctor en camino indicator */}
        {patient.isDoctorEnCamino && (
          <div className="mt-4 flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400/15 border border-amber-400/30 animate-pulse">
            <svg className="w-4 h-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span className="text-amber-400 font-bold text-xs uppercase tracking-widest">Médico informará pronto</span>
          </div>
        )}
      </div>

      {/* Page dots for multiple patients */}
      {patients.length > 1 && (
        <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-2">
          {patients.map((_, i) => (
            <div
              key={i}
              className="h-1.5 rounded-full transition-all duration-500 cursor-pointer"
              onClick={() => { setFocusIdx(i); setVisible(true); }}
              style={{
                width: i === focusIdx ? '24px' : '6px',
                backgroundColor: i === focusIdx ? primaryColor : (theme === 'dark' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.20)')
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// LAYOUT 4: TICKER_ONLY — Branded background + scrolling ticker strip
// ─────────────────────────────────────────────────────────────────────────────

export const TickerOnlyLayout: React.FC<BoardLayoutProps & {
  hospitalName: string;
  logoUrl?: string;
  logoDarkUrl?: string;
}> = ({
  patients, theme, t, primaryColor, getStatusDisplay,
  adAssets, adSettings, adsEnabled, hospitalId,
  hospitalName, logoUrl, logoDarkUrl
}) => {
  // Build a ticker string from all active patients
  const summaryItems = patients.map(p =>
    `${p.id} — ${getStatusDisplay(p)}`
  );
  const tickerText = summaryItems.length > 0
    ? summaryItems.join('   ·   ')
    : 'Sin procedimientos activos en este momento';

  const effectiveLogo = theme === 'dark' && logoDarkUrl ? logoDarkUrl : logoUrl;

  return (
    <div
      className="relative w-full h-full flex flex-col items-center justify-center overflow-hidden"
      style={{
        background: theme === 'dark'
          ? `radial-gradient(ellipse at 30% 50%, ${primaryColor}15, transparent 60%), radial-gradient(ellipse at 70% 50%, ${primaryColor}08, transparent 60%)`
          : `radial-gradient(ellipse at 30% 50%, ${primaryColor}10, transparent 60%), radial-gradient(ellipse at 70% 50%, ${primaryColor}05, transparent 60%)`
      }}
    >
      {/* Subtle geometric pattern */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 40px, ${primaryColor} 40px, ${primaryColor} 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, ${primaryColor} 40px, ${primaryColor} 41px)`,
        }} />

      {/* Logo + Hospital Name */}
      <div className="z-10 flex flex-col items-center gap-6">
        {effectiveLogo ? (
          <img
            src={effectiveLogo}
            alt={hospitalName}
            className="h-20 md:h-32 object-contain drop-shadow-2xl"
            style={{ filter: theme === 'dark' ? 'drop-shadow(0 0 24px rgba(255,255,255,0.15))' : 'none' }}
          />
        ) : (
          <div
            className="w-20 h-20 md:w-28 md:h-28 rounded-3xl flex items-center justify-center shadow-2xl"
            style={{ backgroundColor: primaryColor }}
          >
            <svg className="w-10 h-10 md:w-14 md:h-14 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </div>
        )}

        <div className="text-center">
          <h2
            className={`font-black tracking-tight ${t.textMain}`}
            style={{ fontSize: 'clamp(1.5rem, 3.5vw, 3.5rem)' }}
          >
            {hospitalName}
          </h2>
          <p className={`text-sm ${t.textSubtle} mt-1 font-medium`}>
            Monitoreo de Procedimientos en tiempo real
          </p>
        </div>

        {/* Live indicator */}
        <div
          className="flex items-center gap-2.5 px-5 py-2.5 rounded-full border"
          style={{ backgroundColor: primaryColor + '15', borderColor: primaryColor + '30' }}
        >
          <span className="w-2 h-2 rounded-full animate-ping" style={{ backgroundColor: primaryColor }} />
          <span className="font-bold uppercase tracking-widest text-xs" style={{ color: primaryColor }}>
            EN VIVO — {patients.length} procedimiento{patients.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Ticker strip at bottom */}
      <div
        className="absolute bottom-0 left-0 right-0 flex items-center overflow-hidden h-14 md:h-16"
        style={{
          background: theme === 'dark'
            ? `linear-gradient(90deg, ${primaryColor}40, ${primaryColor}25, ${primaryColor}40)`
            : `linear-gradient(90deg, ${primaryColor}30, ${primaryColor}18, ${primaryColor}30)`,
          borderTop: `1px solid ${primaryColor}30`
        }}
      >
        {/* Left label */}
        <div
          className="shrink-0 px-5 border-r h-full flex items-center font-black uppercase tracking-widest text-xs"
          style={{ borderColor: primaryColor + '30', color: primaryColor }}
        >
          ESTADO
        </div>

        {/* Scrolling text */}
        <div className="flex-1 overflow-hidden relative">
          <div
            className={`whitespace-nowrap font-bold ${t.textMain} animate-[ticker_30s_linear_infinite]`}
            style={{ fontSize: 'clamp(0.8rem, 1.2vw, 1.1rem)' }}
          >
            {/* Duplicate for seamless loop */}
            {tickerText}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{tickerText}
          </div>
        </div>

        {/* Right: patient count pill */}
        <div className="shrink-0 px-4">
          <span
            className="text-xs font-black rounded-full px-3 py-1"
            style={{ backgroundColor: primaryColor, color: 'white' }}
          >
            {patients.length}
          </span>
        </div>
      </div>
    </div>
  );
};
